import { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react'
import axios from '@/api/api'
import { API_ROUTES } from '@/api/routes'

const SessionCtx = createContext({
    user: null,
    loading: true,
    refreshSession: async () => { },
    checkSession: async () => { }
})

export const useSession = () => useContext(SessionCtx)

export default function SessionProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const started = useRef(false)

    // ✅ Función para verificar sesión actual (usa /auth/me)
    const checkSession = useCallback(async () => {
        try {
            // console.log('📡 Llamando a:', API_ROUTES.me); // ← Debug
            const { data } = await axios.get(API_ROUTES.me, { withCredentials: true })
            // console.log('✅ Sesión verificada:', data);

            // ⚠️ Validar que sea un objeto, no HTML
            if (typeof data === 'string' && data.includes('<!DOCTYPE html>')) {
                console.error('❌ ERROR: Recibimos HTML en lugar de JSON');
                setUser(null);
                return null;
            }

            setUser(data || null)
            return data || null
        } catch (error) {
            // console.log('ℹ️ No hay sesión activa:', error);
            setUser(null)
            return null
        } finally {
            setLoading(false)
        }
    }, [])

    // ✅ Función para renovar sesión (usa /auth/refresh-session)
    const refreshSession = useCallback(async () => {
        try {
            const { data } = await axios.get(API_ROUTES.refreshSession, { withCredentials: true })
            // console.log('✅ Sesión renovada:', data?.user);
            setUser(data?.user || null)
            return data?.user || null
        } catch (error) {
            // console.log('ℹ️ Error al renovar sesión:', error);
            setUser(null)
            throw new Error('No session')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        if (started.current) return
        started.current = true
            ; (async () => {
                try {
                    // Usar checkSession en lugar de refreshSession para el inicio
                    await checkSession()
                } catch {
                    setUser(null)
                } finally {
                    setLoading(false)
                }
            })()
    }, [checkSession])

    const value = useMemo(() => ({
        user,
        loading,
        refreshSession,
        checkSession
    }), [user, loading, refreshSession, checkSession])

    return (
        <SessionCtx.Provider value={value}>
            {children}
        </SessionCtx.Provider>
    )
}